import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

export type ViewMode = 'default' | 'branch';

const VIEW_MODE_STORAGE_KEY = 'blueride_view_mode';

@Injectable({
  providedIn: 'root'
})
export class ViewModeService {
  private readonly viewModeSubject = new BehaviorSubject<ViewMode>(
    this.getStoredViewMode()
  );

  readonly viewMode$: Observable<ViewMode> = this.viewModeSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  get currentViewMode(): ViewMode {
    return this.viewModeSubject.value;
  }

  constructor(private router: Router) {
    this.syncViewModeWithRoute();
  }

  setViewMode(mode: ViewMode): void {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.error('Error storing view mode:', error);
    }
    this.viewModeSubject.next(mode);
  }

  resetViewMode(): void {
    localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
    this.viewModeSubject.next('default');
  }

  private syncViewModeWithRoute(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const isBranchRoute = event.urlAfterRedirects.startsWith('/branch-view');
        const expectedMode: ViewMode = isBranchRoute ? 'branch' : 'default';
        if (this.viewModeSubject.value !== expectedMode) {
          this.setViewMode(expectedMode);
        }
      });
  }

  private getStoredViewMode(): ViewMode {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'branch') {
        return 'branch';
      }
    } catch {
      localStorage.removeItem(VIEW_MODE_STORAGE_KEY);
    }
    return 'default';
  }
}
